import {Decimal} from 'decimal.js';
import {match, Pattern} from 'ts-pattern';
import {z} from 'zod';

import {isNil, isNotNil} from 'ramda';

import {NonNegativeBigDecimalSchema, NonNegativeMoneySchema} from '@dms/api/shared';

export const VehicleFinancingTypeSchema = z.enum(['STANDARD', 'BALLOON']);
export type VehicleFinancingType = z.infer<typeof VehicleFinancingTypeSchema>;

export const FinancingConfigurationModeSchema = z.enum(['TEMPLATE', 'MANUAL']);
export type FinancingConfigurationMode = z.infer<typeof FinancingConfigurationModeSchema>;

const PercentageSchema = z.number().int().min(0).max(90);
const DownPaymentBracketSchema = PercentageSchema.multipleOf(10);
const BalloonPaymentBracketSchema = z.number().int().min(10).max(60).multipleOf(10);
const ApiDateSchema = z.string().date().nullable();

function validateDateInterval(
  value: {validFrom: string | null; validTo: string | null},
  context: z.RefinementCtx
) {
  if (value.validFrom !== null && value.validTo !== null && value.validFrom > value.validTo) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Valid from must not be after valid to',
      path: ['validTo'],
    });
  }
}

export const FinancingRateMatrixCellSchema = z.object({
  term: z.number().int().positive(),
  downPayment: DownPaymentBracketSchema,
  balloonPayment: BalloonPaymentBracketSchema.nullable(),
  nominalRate: NonNegativeBigDecimalSchema,
  apr: NonNegativeBigDecimalSchema,
});
export type FinancingRateMatrixCell = z.infer<typeof FinancingRateMatrixCellSchema>;

function addFinancingRateMatrixUniquenessIssues(
  matrix: {availableTerms: number[]; cells: FinancingRateMatrixCell[]},
  context: z.RefinementCtx,
  pathPrefix: string[]
) {
  if (new Set(matrix.availableTerms).size !== matrix.availableTerms.length) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Available financing terms must be unique',
      path: [...pathPrefix, 'availableTerms'],
    });
  }

  const coordinates = matrix.cells.map(
    (cell) => `${cell.term}:${cell.downPayment}:${cell.balloonPayment ?? 'STANDARD'}`
  );

  if (new Set(coordinates).size !== coordinates.length) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Financing matrix coordinates must be unique',
      path: [...pathPrefix, 'cells'],
    });
  }
}

export const FinancingRateMatrixSchema = z
  .object({
    availableTerms: z.array(z.number().int().positive()),
    cells: z.array(FinancingRateMatrixCellSchema),
  })
  .superRefine((matrix, context) => {
    addFinancingRateMatrixUniquenessIssues(matrix, context, []);

    matrix.cells.forEach((cell, index) => {
      if (cell.balloonPayment !== null && cell.downPayment + cell.balloonPayment >= 100) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Down payment and balloon payment must total less than 100%',
          path: ['cells', index, 'balloonPayment'],
        });
      }
    });
  });
export type FinancingRateMatrix = z.infer<typeof FinancingRateMatrixSchema>;

export const SaleVehicleFinancingConfigurationSchema = z
  .object({
    id: z.string(),
    persisted: z.boolean().optional(),
    type: VehicleFinancingTypeSchema,
    mode: FinancingConfigurationModeSchema,
    templateId: z.string().nullable(),
    financialInstitutionCodeId: z.string().nullable(),
    validTo: ApiDateSchema,
    availableTerms: z.array(z.number().int().positive()),
    rateMatrixCells: z.array(FinancingRateMatrixCellSchema),
    activeTerm: z.number().int().positive().nullable(),
    activeDownPayment: DownPaymentBracketSchema.nullable(),
    activeBalloonPayment: BalloonPaymentBracketSchema.nullable(),
    processingFeeOverride: NonNegativeMoneySchema.nullable(),
    interestRateTypeOverride: z.enum(['FIXED', 'VARIABLE', 'COMBINED']).nullable(),
    vehicleTransferIncludedOverride: z.boolean().nullable(),
    vehicleTransferAmountOverride: NonNegativeMoneySchema.nullable(),
    vehicleRegistrationIncludedOverride: z.boolean().nullable(),
    vehicleRegistrationAmountOverride: NonNegativeMoneySchema.nullable(),
    vehicleDeliveryIncludedOverride: z.boolean().nullable(),
    vehicleDeliveryAmountOverride: NonNegativeMoneySchema.nullable(),
    tradeInPossibleOverride: z.boolean().nullable(),
    creditProtectionIncluded: z.boolean(),
    insuranceIncludedInCalculation: z.boolean(),
    liabilityInsuranceMonthly: NonNegativeMoneySchema.nullable(),
    liabilityInsuranceDeductible: NonNegativeMoneySchema.nullable(),
    comprehensiveInsuranceMonthly: NonNegativeMoneySchema.nullable(),
    comprehensiveInsuranceDeductible: NonNegativeMoneySchema.nullable(),
  })
  .superRefine((configuration, context) => {
    const rateMatrix = {
      availableTerms: configuration.availableTerms,
      cells: configuration.rateMatrixCells,
    };

    addFinancingRateMatrixUniquenessIssues(rateMatrix, context, ['rateMatrix']);
    validateFinancingConfiguration(
      {
        type: configuration.type,
        mode: configuration.mode,
        templateId: configuration.templateId,
        rateMatrix,
        activeTerm: configuration.activeTerm,
        activeDownPayment: configuration.activeDownPayment,
        activeBalloonPayment: configuration.activeBalloonPayment,
      },
      context
    );
  });
export type SaleVehicleFinancingConfiguration = z.infer<
  typeof SaleVehicleFinancingConfigurationSchema
>;

export const SaleVehicleFinancingStateSchema = z.enum([
  'EXPLICIT',
  'AUTOMATIC',
  'EXPIRED',
  'CLEARED',
  'NONE',
]);
export type SaleVehicleFinancingState = z.infer<typeof SaleVehicleFinancingStateSchema>;

export const FinancingSuppressionReasonSchema = z.enum([
  'USER_CLEARED',
  'USER_SELECTED_AUTOMATIC',
  'TEMPLATE_EXPIRED',
  'TEMPLATE_ARCHIVED',
  'VEHICLE_OFFER_EXPIRED',
]);
export type FinancingSuppressionReason = z.infer<typeof FinancingSuppressionReasonSchema>;

export const SaleVehicleFinancingTypeStateSchema = z.object({
  type: VehicleFinancingTypeSchema,
  state: SaleVehicleFinancingStateSchema,
  suppressionReason: FinancingSuppressionReasonSchema.nullable(),
  sourceTemplateId: z.string().nullable(),
});
export type SaleVehicleFinancingTypeState = z.infer<typeof SaleVehicleFinancingTypeStateSchema>;

export const SaleVehicleFinancingOverviewSchema = z
  .object({
    saleVehicleId: z.string(),
    vehiclePrice: NonNegativeMoneySchema.nullable(),
    configurations: z.array(SaleVehicleFinancingConfigurationSchema),
    states: z.array(SaleVehicleFinancingTypeStateSchema),
  })
  .superRefine((overview, context) => {
    const configurationTypes = overview.configurations.map((configuration) => configuration.type);

    if (new Set(configurationTypes).size !== configurationTypes.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Sale vehicle financing configuration types must be unique',
        path: ['configurations'],
      });
    }

    const stateTypes = overview.states.map((state) => state.type);
    if (
      new Set(stateTypes).size !== stateTypes.length ||
      !(['STANDARD', 'BALLOON'] as const).every((type) => stateTypes.includes(type))
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Sale vehicle financing states must contain each financing type exactly once',
        path: ['states'],
      });
    }
  });
export type SaleVehicleFinancingOverview = z.infer<typeof SaleVehicleFinancingOverviewSchema>;

export const SaveSaleVehicleFinancingBodySchema = z
  .object({
    type: VehicleFinancingTypeSchema,
    mode: FinancingConfigurationModeSchema,
    templateId: z.string().nullable(),
    financialInstitutionCodeId: z.string().nullable(),
    validTo: ApiDateSchema,
    rateMatrix: FinancingRateMatrixSchema,
    activeTerm: z.number().int().positive().nullable(),
    activeDownPayment: DownPaymentBracketSchema.nullable(),
    activeBalloonPayment: BalloonPaymentBracketSchema.nullable(),
    processingFeeOverride: NonNegativeMoneySchema.nullable(),
    interestRateTypeOverride: z.enum(['FIXED', 'VARIABLE', 'COMBINED']).nullable(),
    vehicleTransferIncludedOverride: z.boolean().nullable(),
    vehicleTransferAmountOverride: NonNegativeMoneySchema.nullable(),
    vehicleRegistrationIncludedOverride: z.boolean().nullable(),
    vehicleRegistrationAmountOverride: NonNegativeMoneySchema.nullable(),
    vehicleDeliveryIncludedOverride: z.boolean().nullable(),
    vehicleDeliveryAmountOverride: NonNegativeMoneySchema.nullable(),
    tradeInPossibleOverride: z.boolean().nullable(),
    creditProtectionIncluded: z.boolean(),
    insuranceIncludedInCalculation: z.boolean(),
    liabilityInsuranceMonthly: NonNegativeMoneySchema.nullable(),
    liabilityInsuranceDeductible: NonNegativeMoneySchema.nullable(),
    comprehensiveInsuranceMonthly: NonNegativeMoneySchema.nullable(),
    comprehensiveInsuranceDeductible: NonNegativeMoneySchema.nullable(),
  })
  .superRefine((body, context) => {
    validateFinancingConfiguration(body, context);

    if (body.rateMatrix.cells.length > 0 && body.activeTerm === null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A configured financing matrix requires an active variant',
        path: ['activeTerm'],
      });
    }

    if (body.mode === 'TEMPLATE' && body.financialInstitutionCodeId !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Template financing cannot override the financial institution',
        path: ['financialInstitutionCodeId'],
      });
    }

    if (body.mode === 'TEMPLATE' && body.validTo !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Template financing cannot override validity',
        path: ['validTo'],
      });
    }
  });
export type SaveSaleVehicleFinancingBody = z.infer<typeof SaveSaleVehicleFinancingBodySchema>;

interface FinancingConfigurationValidationValue {
  type: VehicleFinancingType;
  mode: FinancingConfigurationMode;
  templateId: string | null;
  rateMatrix: FinancingRateMatrix;
  activeTerm: number | null;
  activeDownPayment: number | null;
  activeBalloonPayment: number | null;
}

function validateFinancingConfiguration(
  body: FinancingConfigurationValidationValue,
  context: z.RefinementCtx
) {
  match([body.mode, body.templateId] as const)
    .with(['MANUAL', Pattern.string], () =>
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Manual financing cannot reference a template',
        path: ['templateId'],
      })
    )
    .with(['TEMPLATE', null], () =>
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Template financing requires a template ID',
        path: ['templateId'],
      })
    )
    .otherwise(() => undefined);

  match(body.type)
    .with('STANDARD', () => {
      if (body.rateMatrix.cells.some((cell) => cell.balloonPayment !== null)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Standard financing cells cannot contain a balloon payment',
          path: ['rateMatrix', 'cells'],
        });
      }
    })
    .with('BALLOON', () => {
      if (body.rateMatrix.cells.some((cell) => cell.balloonPayment === null)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Balloon financing cells require a balloon payment',
          path: ['rateMatrix', 'cells'],
        });
      }
    })
    .exhaustive();

  match([body.activeTerm, body.activeDownPayment] as const)
    .with([null, null], () => {
      if (body.activeBalloonPayment !== null) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'An active balloon payment requires an active financing cell',
          path: ['activeBalloonPayment'],
        });
      }
    })
    .with([Pattern.number, Pattern.number], ([activeTerm, activeDownPayment]) => {
      const hasActiveCell = body.rateMatrix.cells.some(
        (cell) =>
          cell.term === activeTerm &&
          cell.downPayment === activeDownPayment &&
          cell.balloonPayment === body.activeBalloonPayment
      );

      if (!hasActiveCell) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'The active financing tuple must reference a matrix cell',
          path: ['activeTerm'],
        });
      }
    })
    .otherwise(() =>
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Active term and down payment must be set together',
        path: ['activeTerm'],
      })
    );
}

export const LeasingTargetGroupSchema = z.enum(['PRIVATE', 'COMMERCIAL', 'BOTH']);
export type LeasingTargetGroup = z.infer<typeof LeasingTargetGroupSchema>;

export const LeasingPrimaryTargetGroupSchema = z.enum(['PRIVATE', 'COMMERCIAL']);
export type LeasingPrimaryTargetGroup = z.infer<typeof LeasingPrimaryTargetGroupSchema>;

export const LEASING_TERMS = [12, 24, 36, 48, 60, 72];
export const LEASING_ANNUAL_MILEAGES = [5000, 7500, 10000, 15000, 20000, 25000, 30000];

const leasingTerms = new Set(LEASING_TERMS);
const leasingAnnualMileages = new Set(LEASING_ANNUAL_MILEAGES);

export const LeasingRateMatrixCellSchema = z.object({
  term: z
    .number()
    .int()
    .refine((term) => leasingTerms.has(term)),
  annualMileage: z
    .number()
    .int()
    .refine((annualMileage) => leasingAnnualMileages.has(annualMileage)),
  monthlyRate: NonNegativeMoneySchema,
  nominalRate: NonNegativeBigDecimalSchema.nullable(),
  apr: NonNegativeBigDecimalSchema.nullable(),
});
export type LeasingRateMatrixCell = z.infer<typeof LeasingRateMatrixCellSchema>;

export const LeasingCommonParametersSchema = z.object({
  listPrice: NonNegativeMoneySchema.nullable(),
  vatRate: NonNegativeBigDecimalSchema.nullable(),
  downPayment: NonNegativeMoneySchema.nullable(),
  excessMileageCost: NonNegativeMoneySchema.nullable(),
  underMileageCredit: NonNegativeMoneySchema.nullable(),
  vehicleTransferIncluded: z.boolean(),
  vehicleTransferAmount: NonNegativeMoneySchema.nullable(),
  vehicleRegistrationIncluded: z.boolean(),
  vehicleRegistrationAmount: NonNegativeMoneySchema.nullable(),
  vehicleDeliveryIncluded: z.boolean(),
  vehicleDeliveryAmount: NonNegativeMoneySchema.nullable(),
  maintenanceMonthly: NonNegativeMoneySchema.nullable(),
  wearMonthly: NonNegativeMoneySchema.nullable(),
  maintenanceAndWearMonthly: NonNegativeMoneySchema.nullable(),
  vehicleTaxMonthly: NonNegativeMoneySchema.nullable(),
  liabilityInsuranceMonthly: NonNegativeMoneySchema.nullable(),
  liabilityInsuranceDeductible: NonNegativeMoneySchema.nullable(),
  comprehensiveInsuranceMonthly: NonNegativeMoneySchema.nullable(),
  comprehensiveInsuranceDeductible: NonNegativeMoneySchema.nullable(),
  tiresMonthly: NonNegativeMoneySchema.nullable(),
  inspectionMonthly: NonNegativeMoneySchema.nullable(),
  returnValueInsuranceMonthly: NonNegativeMoneySchema.nullable(),
  specialConditionCodes: z.array(z.string()),
  tradeInPossible: z.boolean(),
  internalNotes: z.string().nullable(),
});
export type LeasingCommonParameters = z.infer<typeof LeasingCommonParametersSchema>;

export const SaleVehicleLeasingConfigurationSchema = z
  .object({
    id: z.string(),
    financialInstitutionCodeId: z.string().nullable(),
    targetGroup: LeasingTargetGroupSchema,
    primaryTargetGroup: LeasingPrimaryTargetGroupSchema.nullable(),
    validFrom: ApiDateSchema,
    validTo: ApiDateSchema,
    activeTerm: z.number().int().positive().nullable(),
    activeAnnualMileage: z.number().int().positive().nullable(),
    rateMatrixCells: z.array(LeasingRateMatrixCellSchema),
    commonParameters: LeasingCommonParametersSchema,
  })
  .superRefine(validateLeasingConfiguration);
export type SaleVehicleLeasingConfiguration = z.infer<typeof SaleVehicleLeasingConfigurationSchema>;

export const SaleVehicleLeasingStateSchema = z.enum(['CURRENT', 'EXPIRED', 'NONE']);
export type SaleVehicleLeasingState = z.infer<typeof SaleVehicleLeasingStateSchema>;

export const SaleVehicleLeasingOverviewSchema = z.object({
  saleVehicleId: z.string(),
  state: SaleVehicleLeasingStateSchema,
  configuration: SaleVehicleLeasingConfigurationSchema.nullable(),
});
export type SaleVehicleLeasingOverview = z.infer<typeof SaleVehicleLeasingOverviewSchema>;

export const SaveSaleVehicleLeasingBodySchema = z
  .object({
    financialInstitutionCodeId: z.string().min(1).nullable(),
    targetGroup: LeasingTargetGroupSchema,
    primaryTargetGroup: LeasingPrimaryTargetGroupSchema.nullable(),
    validFrom: ApiDateSchema,
    validTo: ApiDateSchema,
    activeTerm: z.number().int().positive().nullable(),
    activeAnnualMileage: z.number().int().positive().nullable(),
    rateMatrixCells: z.array(LeasingRateMatrixCellSchema),
    commonParameters: LeasingCommonParametersSchema,
  })
  .superRefine((body, context) => {
    if (isNil(body.financialInstitutionCodeId)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Financial institution is required',
        path: ['financialInstitutionCodeId'],
      });
    }

    validateLeasingConfiguration(body, context);
  });
export type SaveSaleVehicleLeasingBody = z.infer<typeof SaveSaleVehicleLeasingBodySchema>;

interface LeasingConfigurationValidationValue {
  targetGroup: LeasingTargetGroup;
  primaryTargetGroup: LeasingPrimaryTargetGroup | null;
  validFrom: string | null;
  validTo: string | null;
  activeTerm: number | null;
  activeAnnualMileage: number | null;
  rateMatrixCells: LeasingRateMatrixCell[];
  commonParameters: LeasingCommonParameters;
}

function validateLeasingConfiguration(
  body: LeasingConfigurationValidationValue,
  context: z.RefinementCtx
) {
  validateDateInterval(body, context);

  match([body.targetGroup, body.primaryTargetGroup] as const)
    .with(['BOTH', null], () =>
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Both target groups require a primary target group',
        path: ['primaryTargetGroup'],
      })
    )
    .with([Pattern.union('PRIVATE', 'COMMERCIAL'), Pattern.not(null)], () =>
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A single target group cannot have a hidden primary target group',
        path: ['primaryTargetGroup'],
      })
    )
    .otherwise(() => undefined);

  const coordinates = body.rateMatrixCells.map((cell) => `${cell.term}:${cell.annualMileage}`);

  if (new Set(coordinates).size !== coordinates.length) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Leasing matrix coordinates must be unique',
      path: ['rateMatrixCells'],
    });
  }

  match([body.activeTerm, body.activeAnnualMileage] as const)
    .with([null, null], () => {
      if (body.rateMatrixCells.length > 0) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'A non-empty leasing matrix requires an active selection',
          path: ['activeTerm'],
        });
      }
    })
    .with([Pattern.number, Pattern.number], ([activeTerm, activeAnnualMileage]) => {
      const hasActiveCell = body.rateMatrixCells.some(
        (cell) => cell.term === activeTerm && cell.annualMileage === activeAnnualMileage
      );

      if (!hasActiveCell) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'The active leasing tuple must reference a matrix cell',
          path: ['activeTerm'],
        });
      }
    })
    .otherwise(() =>
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Active leasing term and annual mileage must be set together',
        path: ['activeTerm'],
      })
    );

  if (
    new Set(body.commonParameters.specialConditionCodes).size !==
    body.commonParameters.specialConditionCodes.length
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Leasing special-condition codes must be unique',
      path: ['commonParameters', 'specialConditionCodes'],
    });
  }

  const {listPrice, downPayment} = body.commonParameters;

  if (isNotNil(listPrice) && isNotNil(downPayment)) {
    if (listPrice.currency !== downPayment.currency) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Leasing down payment must use the list-price currency',
        path: ['commonParameters', 'downPayment', 'currency'],
      });
    }

    if (new Decimal(downPayment.amount).greaterThan(listPrice.amount)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Leasing down payment must not exceed the list price',
        path: ['commonParameters', 'downPayment', 'amount'],
      });
    }
  }
}

export type GetSaleVehicleFinancingApiArg = {saleVehicleId: string};
export type GetSaleVehicleFinancingApiResponse = SaleVehicleFinancingOverview;
export type SaveSaleVehicleFinancingApiArg = {
  saleVehicleId: string;
  body: SaveSaleVehicleFinancingBody;
};
export type SaveSaleVehicleFinancingApiResponse = void;
export type DeleteSaleVehicleFinancingApiArg = {
  saleVehicleId: string;
  type: VehicleFinancingType;
};
export type DeleteSaleVehicleFinancingApiResponse = void;
export type ActivateAutomaticSaleVehicleFinancingApiArg = {
  saleVehicleId: string;
  type: VehicleFinancingType;
};
export type ActivateAutomaticSaleVehicleFinancingApiResponse = void;
export type GetSaleVehicleLeasingApiArg = {saleVehicleId: string};
export type GetSaleVehicleLeasingApiResponse = SaleVehicleLeasingOverview;
export type SaveSaleVehicleLeasingApiArg = {
  saleVehicleId: string;
  body: SaveSaleVehicleLeasingBody;
};
export type SaveSaleVehicleLeasingApiResponse = void;
export type DeleteSaleVehicleLeasingApiArg = {saleVehicleId: string};
export type DeleteSaleVehicleLeasingApiResponse = void;
